import React, { useCallback, useEffect, useState } from 'react'
import { Sparkles, Loader2, X } from 'lucide-react'
import agentService, { AgentPredictionProposal } from '../../services/agentService'
import { useTranslation } from '../../hooks/use-i18n'

interface PredictionCopilotPanelProps {
  roomId: string
  matchId: string
  teamA: string
  teamB: string
  onApplyProposal: (proposal: AgentPredictionProposal) => void
}

const PredictionCopilotPanel: React.FC<PredictionCopilotPanelProps> = ({
  roomId,
  matchId,
  teamA,
  teamB,
  onApplyProposal,
}) => {
  const { t, locale } = useTranslation()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [proposal, setProposal] = useState<AgentPredictionProposal | null>(null)
  const [sources, setSources] = useState<string[]>([])
  const [fromTemplate, setFromTemplate] = useState(false)
  const [question, setQuestion] = useState('')
  const [sessionId, setSessionId] = useState<string | undefined>()
  const [streaming, setStreaming] = useState(false)

  useEffect(() => {
    let cancelled = false
    agentService
      .getStatus()
      .then((s) => {
        if (!cancelled) {
          setEnabled(s.enabled)
          setStreaming(Boolean(s.streaming))
        }
      })
      .catch(() => {
        if (!cancelled) setEnabled(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const fetchCopilot = useCallback(async () => {
    setLoading(true)
    setError(null)
    setMessage('')
    setProposal(null)
    setSources([])

    const params = {
      roomId,
      matchId,
      sessionId,
      question: question.trim() || undefined,
      locale,
    }

    try {
      if (streaming) {
        await agentService.predictionCopilotStream(params, {
          onMeta: (meta) => {
            setSessionId(meta.sessionId)
            setProposal(meta.proposal ?? null)
            setSources(meta.sources ?? [])
            setFromTemplate(meta.fromTemplate)
            setLoading(false)
          },
          onToken: (text) => {
            setMessage((prev) => (prev ?? '') + text)
          },
          onDone: (res) => {
            setMessage(res.message)
            setProposal(res.proposal ?? null)
            setSources(res.sources ?? [])
            setFromTemplate(res.fromTemplate)
            setSessionId(res.sessionId)
            setLoading(false)
          },
          onError: (msg) => setError(msg),
        })
      } else {
        const res = await agentService.predictionCopilot(params)
        setMessage(res.message)
        setProposal(res.proposal ?? null)
        setSources(res.sources ?? [])
        setFromTemplate(res.fromTemplate)
        setSessionId(res.sessionId)
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ??
        (e as Error).message ??
        t('agent.errorGeneric')
      setError(typeof msg === 'string' ? msg : t('agent.errorGeneric'))
    } finally {
      setLoading(false)
    }
  }, [roomId, matchId, question, locale, sessionId, streaming, t])

  const handleOpen = () => {
    setOpen(true)
    if (!message && !loading) {
      void fetchCopilot()
    }
  }

  if (enabled === false) return null

  return (
    <div className="mb-4">
      {!open ? (
        <button
          type="button"
          onClick={handleOpen}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
        >
          <Sparkles className="w-4 h-4 text-amber-300" />
          {t('agent.askCopilot')}
        </button>
      ) : (
        <div className="rounded-xl border border-white/15 bg-black/30 p-4 text-white">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-300 flex-shrink-0" />
              <span className="text-sm font-semibold">{t('agent.copilotTitle')}</span>
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-white/70">
                {t('agent.aiBadge')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg hover:bg-white/10 text-white/70"
              aria-label={t('agent.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('agent.questionPlaceholder')}
              className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm text-white placeholder:text-white/40"
            />
            <button
              type="button"
              onClick={() => void fetchCopilot()}
              disabled={loading}
              className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
            >
              {t('agent.ask')}
            </button>
          </div>

          {loading && !message && (
            <div className="flex items-center gap-2 text-white/70 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('agent.loading')}
            </div>
          )}

          {error && (
            <p className="text-sm text-red-300 py-2">{error}</p>
          )}

          {(message || (loading && message)) && (
            <div className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed mb-3">
              {message}
              {loading && message && (
                <span className="inline-block w-1.5 h-4 ml-0.5 bg-white/60 animate-pulse align-middle" />
              )}
            </div>
          )}

          {sources.length > 0 && message && (
            <p className="text-xs text-white/50 mb-3">
              {t('agent.sources')}: {sources.join(' · ')}
              {fromTemplate ? ` · ${t('agent.templateFallback')}` : ''}
            </p>
          )}

          {proposal && message && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/10">
              <span className="text-xs text-white/60">
                {proposal.winner ?? t('predictions.draw')} · {proposal.predictedScore}
              </span>
              <button
                type="button"
                onClick={() => onApplyProposal(proposal)}
                className="ml-auto px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold"
              >
                {t('agent.applySuggestion')}
              </button>
              <button
                type="button"
                onClick={() =>
                  void agentService
                    .explainScoring({
                      roomId,
                      predictedScore: proposal.predictedScore,
                      winner: proposal.winner,
                      locale,
                    })
                    .then((res) => {
                      setMessage((prev) => `${prev ?? ''}\n\n---\n\n${res.message}`)
                    })
                    .catch(() => {})
                }
                className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs"
              >
                {t('agent.explainPoints')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PredictionCopilotPanel
